import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import FileViewer from "react-file-viewer";
import {
  FileViewerHandle,
  FileViewerAdapterProps,
} from "../FilePreviewer.types";
import { CSS_CLASSES, ZOOM_CONFIG } from "../FilePreviewer.config";

const FileViewerAdapter = React.forwardRef<
  FileViewerHandle,
  FileViewerAdapterProps
>(({ filePath, fileType, fileName, onZoomChange }, ref) => {
  // Zoom Level state
  const [zoomLevel, setZoomLevel] = useState<number>(ZOOM_CONFIG.DEFAULT);
  const scaleFactor = zoomLevel / 100;

  // Reference for the print content
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onZoomChange) {
      onZoomChange(Math.round(zoomLevel));
    }
  }, [zoomLevel, onZoomChange]);

  // Use react-to-print
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "file",
    removeAfterPrint: true,
  });

  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      setZoomLevel(prev => Math.min(prev + ZOOM_CONFIG.STEP, ZOOM_CONFIG.MAX));
    },
    zoomOut: () => {
      setZoomLevel(prev => {
        const newZoom = Math.max(prev - ZOOM_CONFIG.STEP, ZOOM_CONFIG.MIN);
        if (onZoomChange) {
          onZoomChange(Math.round(newZoom));
        }
        return newZoom;
      });
    },
    zoomReset: () => {
      setZoomLevel(ZOOM_CONFIG.DEFAULT);
      if (onZoomChange) {
        onZoomChange(Math.round(ZOOM_CONFIG.DEFAULT));
      }
    },
    print: () => {
      // setZoomLevel(ZOOM_CONFIG.DEFAULT);
      handlePrint && handlePrint();
    },
    download: () => {
      const link = document.createElement("a");
      link.href = filePath;
      link.download = fileName ?? "download" + `.${fileType}`;
      link.click();
    },
  }));

  return (
    <div
      ref={printRef}
      className={`${CSS_CLASSES.REACT_FILE_VIEWER} ${CSS_CLASSES.IMAGE_ZOOM_CONTAINER}`}
      style={{
        transform: `scale(${scaleFactor})`,
        transformOrigin: "top left",
        ...(scaleFactor != 1
          ? {
              width: `${100 * scaleFactor}%`,
              height: `${100 * scaleFactor}%`,
            }
          : {}),
      }}
    >
      <FileViewer
        fileType={fileType}
        filePath={filePath}
        onError={() => <div>Could not display file</div>}
      />
    </div>
  );
});

export default FileViewerAdapter;
